/** Read models mirroring apps/api/src/routes-treasury.ts. The client never decides where coins go. */

export interface TreasuryCandidate {
  address: string;
  name: string;
  label: string;
  balance: string | null;
  balanceFormatted: string | null;
  error?: string;
}

export interface MandateView {
  present: boolean;
  treasury: string | null;
  maxPerRedeem: string;
  sessionTotal: string;
  maxRedeems: number;
  validUntil: number;
  payee: string;
  asset: string;
  problem: string | null;
  unavailable: string | null;
}

export interface TreasuryView {
  chainId: number;
  asset: string;
  assetSymbol: string;
  assetDecimals: number;
  coinValue: string;
  person: string | null;
  personName: string | null;
  chosen: string | null;
  chosenName: string | null;
  balance: string | null;
  balanceFormatted: string | null;
  candidates: TreasuryCandidate[];
  discoveryError: string | null;
  create: { mode: 'server' | 'home-portal'; portalUrl: string | null; canName: boolean };
  mandate: MandateView;
  notice: string | null;
  unavailable: string | null;
}

export interface StakeStep {
  step: 'treasury' | 'authority';
  status: 'done' | 'kept' | 'blocked' | 'failed';
  said: string;
  detail?: string;
}

export interface StakeNext {
  action: 'none' | 'create-at-home' | 'authorise-at-home' | 'retry';
  said: string;
  portalUrl?: string | null;
}

export interface StakeResult {
  ready: boolean;
  treasury: string | null;
  treasuryName: string | null;
  balance: string | null;
  steps: StakeStep[];
  next: StakeNext;
}

export type TreasuryStage = 'loading' | 'unavailable' | 'set-up' | 'authorise' | 'ready';

/** Where a person stands with their on-chain coin account, read off the view alone. */
export function treasuryStage(view: TreasuryView | null | undefined): TreasuryStage {
  if (!view) return 'loading';
  if (view.unavailable) return 'unavailable';
  if (!view.chosen) return 'set-up';
  if (view.mandate.unavailable) return 'ready';
  return view.mandate.present && !view.mandate.problem ? 'ready' : 'authorise';
}

export function progressLine(result: StakeResult | null | undefined): string | null {
  if (!result) return null;
  const broke = result.steps.find((s) => s.status === 'failed' || s.status === 'blocked');
  if (broke) return broke.said;
  return result.steps.at(-1)?.said ?? null;
}

/** Base units → whole-coin string at the asset's decimals. */
export function fmtAsset(raw: string | null | undefined, decimals: number): string | null {
  if (raw === null || raw === undefined || !/^-?\d+$/.test(raw.trim())) return null;
  const amount = BigInt(raw.trim());
  const neg = amount < 0n;
  const abs = neg ? -amount : amount;
  const unit = 10n ** BigInt(decimals);
  const whole = (abs / unit).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (decimals === 0) return `${neg ? '-' : ''}${whole}`;
  const frac = (abs % unit).toString().padStart(decimals, '0').slice(0, 2);
  return `${neg ? '-' : ''}${whole}.${frac}`;
}
